import {
  newOutlineSection,
  type SurfOutlineRole,
  type SurfOutlineSection,
  type SurfOutlineVisualKind,
} from "@vida/surf-core";

const OUTLINE_ROLES: SurfOutlineRole[] = [
  "cover",
  "agenda",
  "content",
  "section",
  "comparison",
  "data",
  "quote",
  "summary",
  "cta",
];

const VISUAL_KINDS: SurfOutlineVisualKind[] = [
  "none",
  "image",
  "graphic",
  "chart",
  "table",
];

export type OutlineSectionsEditorProps = {
  sections: SurfOutlineSection[];
  onSectionsChange: (sections: SurfOutlineSection[]) => void;
  showVisualHints?: boolean;
  showRoleFields?: boolean;
  showAddButton?: boolean;
  onRegenerateSection?: (sectionId: string) => void;
};

export function OutlineSectionsEditor({
  sections,
  onSectionsChange,
  showVisualHints = true,
  showRoleFields = true,
  showAddButton = true,
  onRegenerateSection,
}: OutlineSectionsEditorProps) {
  const move = (index: number, delta: number) => {
    const next = [...sections];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const tmp = next[index]!;
    next[index] = next[target]!;
    next[target] = tmp;
    onSectionsChange(next);
  };

  const update = (id: string, patch: Partial<SurfOutlineSection>) => {
    onSectionsChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const updateBullet = (sectionId: string, bulletIndex: number, value: string) => {
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const bullets = [...s.bullets];
        bullets[bulletIndex] = value;
        return { ...s, bullets };
      }),
    );
  };

  const addBullet = (sectionId: string) => {
    onSectionsChange(
      sections.map((s) =>
        s.id === sectionId ? { ...s, bullets: [...s.bullets, ""] } : s,
      ),
    );
  };

  const removeBullet = (sectionId: string, bulletIndex: number) => {
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, bullets: s.bullets.filter((_, i) => i !== bulletIndex) };
      }),
    );
  };

  const updateVisualKind = (sectionId: string, kind: SurfOutlineVisualKind) => {
    onSectionsChange(
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const visual = kind === "none" ? undefined : { ...(s.visual ?? { kind }), kind };
        return { ...s, visual };
      }),
    );
  };

  const remove = (id: string) => {
    onSectionsChange(sections.filter((s) => s.id !== id));
  };

  return (
    <>
      {showAddButton && (
        <div className="wizard-actions">
          <button type="button" onClick={() => onSectionsChange([...sections, newOutlineSection()])}>
            Aggiungi sezione
          </button>
        </div>
      )}

      <ol className="outline-list">
        {sections.map((section, index) => (
          <li key={section.id} className="outline-item">
            <div className="outline-item-toolbar">
              <span className="muted">#{index + 1}</span>
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0}>
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === sections.length - 1}
              >
                ↓
              </button>
              {onRegenerateSection && (
                <button type="button" onClick={() => onRegenerateSection(section.id)}>
                  Rigenera testo slide
                </button>
              )}
              <button type="button" className="danger" onClick={() => remove(section.id)}>
                Elimina
              </button>
            </div>

            {showRoleFields && (
              <div className="outline-fields-row">
                <label className="field-label">
                  Ruolo
                  <select
                    value={section.role}
                    onChange={(e) =>
                      update(section.id, {
                        role: e.target.value as SurfOutlineRole,
                        intent: e.target.value,
                      })
                    }
                  >
                    {OUTLINE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field-label">
                  Layout hint
                  <input
                    className="text-input"
                    value={section.layoutHint ?? ""}
                    placeholder="title-image-right, …"
                    onChange={(e) => update(section.id, { layoutHint: e.target.value })}
                  />
                </label>
              </div>
            )}

            <input
              className="text-input"
              value={section.title}
              placeholder="Titolo slide"
              onChange={(e) => update(section.id, { title: e.target.value })}
            />
            <input
              className="text-input"
              value={section.subtitle ?? ""}
              placeholder="Sottotitolo (opzionale)"
              onChange={(e) => update(section.id, { subtitle: e.target.value })}
            />

            <div className="outline-bullets">
              <div className="outline-bullets-header">
                <span className="field-label">Bullet (3–6 consigliati)</span>
                <button type="button" onClick={() => addBullet(section.id)}>
                  + bullet
                </button>
              </div>
              {section.bullets.map((bullet, bi) => (
                <div key={`${section.id}-b-${bi}`} className="outline-bullet-row">
                  <input
                    className="text-input"
                    value={bullet}
                    placeholder={`Punto ${bi + 1}`}
                    onChange={(e) => updateBullet(section.id, bi, e.target.value)}
                  />
                  <button type="button" onClick={() => removeBullet(section.id, bi)}>
                    ×
                  </button>
                </div>
              ))}
              {section.bullets.length === 0 && (
                <button type="button" onClick={() => addBullet(section.id)}>
                  Aggiungi primo bullet
                </button>
              )}
            </div>

            <textarea
              className="source-area"
              rows={2}
              value={section.body ?? ""}
              placeholder="Corpo aggiuntivo (opzionale)"
              onChange={(e) => update(section.id, { body: e.target.value })}
            />

            <textarea
              className="source-area"
              rows={2}
              value={section.notes ?? ""}
              placeholder="Speaker notes (opzionale)"
              onChange={(e) => update(section.id, { notes: e.target.value })}
            />

            {showVisualHints && (
              <div className="outline-visual-hint">
                <label className="field-label">
                  Visual hint
                  <select
                    value={section.visual?.kind ?? "none"}
                    onChange={(e) =>
                      updateVisualKind(section.id, e.target.value as SurfOutlineVisualKind)
                    }
                  >
                    {VISUAL_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </label>
                {section.visual && section.visual.kind === "image" && (
                  <input
                    className="text-input"
                    value={section.visual.imageSubject ?? ""}
                    placeholder="Soggetto illustrazione"
                    onChange={(e) =>
                      update(section.id, {
                        visual: { ...section.visual!, imageSubject: e.target.value },
                      })
                    }
                  />
                )}
                {section.visual && section.visual.kind !== "none" && (
                  <input
                    className="text-input"
                    value={section.visual.caption ?? ""}
                    placeholder="Didascalia visual"
                    onChange={(e) =>
                      update(section.id, {
                        visual: { ...section.visual!, caption: e.target.value },
                      })
                    }
                  />
                )}
              </div>
            )}

            {showRoleFields && (
              <input
                className="text-input"
                value={section.intent}
                placeholder="Intent editoriale"
                onChange={(e) => update(section.id, { intent: e.target.value })}
              />
            )}
          </li>
        ))}
      </ol>

      {sections.length === 0 && (
        <p className="muted">Nessuna sezione. Genera l&apos;outline o aggiungine una manualmente.</p>
      )}
    </>
  );
}
